import { apiClient } from "./apiClient";
import { syncClock } from "./utils/clockUtils";

/**
 * Obtiene información del partido según el ID
 * Sincroniza el reloj automáticamente cuando hay SoccerData
 * @param {string} matchId - ID del partido
 * @param {string} country - País para filtrar datos (parámetro query)
 * @returns {Promise<Object>} Datos del partido
 */
export const getMatch = async (matchId, country = null) => {
  try {
    // Registrar el momento exacto cuando se inicia la petición
    const requestTime = Date.now();
    
    const response = await apiClient.get(
      `/api/core-clarosports/v2/events/match/${matchId}`,
      {
        params: {
          ...(country === 'us' && { country })
        }
      }
    );
    
    // Registrar el momento exacto cuando se recibe la respuesta
    const responseTime = Date.now();
    
    // Sincronizar el reloj si está disponible SoccerData
    if (response.data?.SoccerData) {
      // Usar responseTime para mayor precisión
      syncClock(response.data.SoccerData, responseTime);
    }
    
    return response.data;
  } catch (error) {
    console.error("Error fetching match data:", error.message);
    throw error;
  }
};

/**
 * Transforma los datos del API a un formato compatible con el componente MatchScore
 * @param {Object} apiData - Datos crudos del API
 * @returns {Object} Datos transformados
 */
export const transformMatchData = (apiData) => {
  return {
    homeTeam: {
      id: apiData.id_local,
      name: apiData.left_name,
      shortName: apiData.left_short_name,
      logo: apiData.left_image,
      score: parseInt(apiData.left_score),
      resultClass: apiData.left_class, // 'clarosports-winner', 'clarosports-loser', 'clarosports-draw'
    },
    awayTeam: {
      id: apiData.id_visita,
      name: apiData.right_name,
      shortName: apiData.right_short_name,
      logo: apiData.right_image,
      score: parseInt(apiData.right_score),
      resultClass: apiData.right_class, // 'clarosports-winner', 'clarosports-loser', 'clarosports-draw'
    },
    score: {
      home: parseInt(apiData.left_score),
      away: parseInt(apiData.right_score),
    },
    competition: apiData.liga,
    date: apiData.utc_date || apiData.date, // Usar utc_date del API
    venue: apiData.venue_name,
    phase: apiData.phase,
    status: apiData.class, // 'clarosports-in-progress-game', 'clarosports-next-game', 'clarosports-finished-game', 'clarosports-error-game'
    periodTitle: apiData.period_title,
    matchName: apiData.game_name,
    matchInfo: {
      venue: apiData.venue_name,
      referee: apiData.referee || "",
    },
    homeTeamInfo: {
      id: apiData.id_local,
      name: apiData.left_name,
      shortName: apiData.left_short_name,
      logo: apiData.left_image,
    },
    awayTeamInfo: {
      id: apiData.id_visita,
      name: apiData.right_name,
      shortName: apiData.right_short_name,
      logo: apiData.right_image,
    },
    events: extractSoccerEvents(apiData),
    soccerData: apiData?.SoccerData || null, // Incluir SoccerData completo para el reloj
  };
};

/**
 * Extrae y transforma los eventos de SoccerData (goles y tarjetas)
 * @param {Object} apiData - Datos crudos del API
 * @returns {Array} Array de eventos ordenados por minuto
 */
export const extractSoccerEvents = (apiData) => {
  const events = [];

  // Extraer goles
  if (apiData.SoccerData?.goals && Array.isArray(apiData.SoccerData.goals)) {
    apiData.SoccerData.goals.forEach(goal => {
      events.push({
        type: "goal",
        eventType: goal.type, // 'G' para gol
        contestantId: goal.contestantId,
        timeMin: goal.timeMin,
        timeMinSec: goal.timeMinSec,
        periodId: goal.periodId,
        scorerId: goal.scorerId,
        scorerName: goal.scorerName,
        timestamp: goal.lastUpdated || goal.timestamp,
      });
    });
  }

  // Extraer tarjetas
  if (apiData.SoccerData?.cards && Array.isArray(apiData.SoccerData.cards)) {
    apiData.SoccerData.cards.forEach(card => {
      events.push({
        type: "card",
        eventType: card.type, // 'YC' para amarilla, 'RC' para roja
        contestantId: card.contestantId,
        timeMin: card.timeMin,
        timeMinSec: card.timeMinSec,
        periodId: card.periodId,
        playerId: card.playerId,
        playerName: card.playerName,
        cardReason: card.cardReason,
        timestamp: card.lastUpdated || card.timestamp,
      });
    });
  }

  // Ordenar eventos por minuto
  return events.sort((a, b) => a.timeMin - b.timeMin);
};

/**
 * Determina si un evento pertenece al equipo local
 * @param {Object} event - Evento del partido
 * @param {string} homeTeamId - ID del equipo local
 * @returns {boolean}
 */
export const isHomeTeamEvent = (event, homeTeamId) => {
  return event.contestantId === homeTeamId;
};

/**
 * Obtiene solo los goles del partido
 * @param {Array} events - Array de eventos
 * @returns {Array} Array solo con eventos de gol
 */
export const getGoalsOnly = (events) => {
  return events.filter(event => event.type === "goal");
};

/**
 * Obtiene solo las tarjetas del partido
 * @param {Array} events - Array de eventos
 * @returns {Array} Array solo con eventos de tarjeta
 */
export const getCardsOnly = (events) => {
  return events.filter(event => event.type === "card");
};

